import { ref, shallowReactive, shallowRef } from 'vue'
import { useLogger } from '@booksy-com/vue-tools'
import { clone, map, pipe, difference, range } from 'ramda'
import moment from 'moment'
import qs from 'qs'

import { getFlag } from '@/lib/flags'
import { useCore } from '@/composables/core'
import { useLoadingScreen } from '@/composables/loading-screen'
import { useAppVisibility } from '@/composables/app-visibility'
import { useEppo } from '@/composables/eppo'

import { useCalendarAnalyticsEvents } from '../calendar-analytics-events/index.js'

import eventProxy from '@/proxies/event.js'

import {
  DATE_FORMAT,
  VIEW_MONTH,
  VIEW_WAITLIST,
  ENDPOINTS,
  FEATURE_FLAGS,
  VIEW_DAY,
} from '@/lib/consts.ts'

import {
  saveMonthData,
  clearAllDays,
  mergeCalendarData,
  clearMonth,
  clearDays,
  checkAndNormalize,
} from './utils.js'

import {
  getCalendarDates,
} from '@/components/calendar/functions.js'

import {
  isFirestoreSupportedForSelectedDays,
} from '../calendar-firestore-data/firestore-data-source/functions/is-firestore-supported-for-selected-days.js'

import { isCalendarFirestoreReady } from '../calendar-firestore-data/is-ready.js'

const CALENDAR_CLEAR_CACHE_FLAG = 'clearCalendarCache'

const DEFAULT_SERVICE_STATE = {
  shouldClearCalendarCache: null,
  checkChangesTimestamp: null,
  version: null,
}

const DEFAULT_STORE = {
  agenda: {},
  bookings: {},
  reservations: {},
  resources: [],
  days: {},
  invalidDays: {},
  open_hours_customizations: {},
  monthCalendar: {},
}

const observedDays = ref([moment().format('YYYY-MM-DD')])
const currentView = ref(VIEW_DAY)

const logger = useLogger('calendar-service')

const store = shallowReactive({})
const shouldClearCalendarCache = ref(DEFAULT_SERVICE_STATE.shouldClearCalendarCache)
const checkChangesTimestamp = ref(DEFAULT_SERVICE_STATE.checkChangesTimestamp)
const version = ref(DEFAULT_SERVICE_STATE.version)

// it stores whether Firestore support is enabled and it’s set when selecting the observed days ("select" function)
const isFirestoreSupportEnabled = shallowRef(false)

const __proxy = {}

const latestCalendarUpdateTimestamp = ref(null)
const observedCalendarIsOutdated = ref(false)
let observedCalendarDaysRange = []
const _calculateOutdatedCalendar = (calendarData) => {
  observedCalendarDaysRange = [
    calendarData.start_date,
    calendarData.end_date,
  ]

  if (!latestCalendarUpdateTimestamp.value) {
    latestCalendarUpdateTimestamp.value = calendarData.timestamp
    return
  }

  const newestUpdatedEvents = Object.values(calendarData.bookings)
    .map((booking) => booking.updated_epoch)
    .filter((updatedEpoch) => moment(updatedEpoch).isAfter(latestCalendarUpdateTimestamp.value))

  if (newestUpdatedEvents.length) {
    const oldest = Math.min(...newestUpdatedEvents)
    const duration = moment.duration(moment.unix(calendarData.timestamp).diff(moment.unix(oldest)))

    useCalendarAnalyticsEvents().sendEventAboutOutdatedCalendar(duration.asSeconds())
  }

  latestCalendarUpdateTimestamp.value = calendarData.timestamp
  observedCalendarIsOutdated.value = false
}

const _checkIfCalendarDataIsOutdatedForObservedDays = (changedDates, timestamp, observedDays) => {
  // shortcut - calendar is already out-of-date
  if (observedCalendarIsOutdated.value) return

  const isOutdated = changedDates.some(day => moment(day).isBetween(
    observedDays[0],
    observedDays[1],
    undefined,
    '[]'
  ))

  observedCalendarIsOutdated.value = isOutdated

  if (!isOutdated) latestCalendarUpdateTimestamp.value = timestamp
}

const _shouldSkipApiCallBecauseFirestore = () => {
  const eppo = useEppo()
  return eppo.ff(FEATURE_FLAGS.Feature_ProviderFirestore)
    && eppo.ff(FEATURE_FLAGS.Feature_CalendarFirestoreIntegration)
    && isCalendarFirestoreReady.value
}

const select = ({ date, view, silent, refresh, filters }) => {
  // shortcut: do not update calendar data on waitlist view
  if (view === VIEW_WAITLIST) {
    return Promise.resolve()
  }

  const days = getCalendarDates(date, view).map(d => d.format(DATE_FORMAT))

  currentView.value = view
  observedDays.value = days
  isFirestoreSupportEnabled.value = isFirestoreSupportedForSelectedDays(days)

  let missing = []

  if (view === VIEW_MONTH) {
    missing.push(days[0], days[days.length - 1])
  } else {
    if (_shouldSkipApiCallBecauseFirestore()) {
      // always refresh when refresh is true and whenever the range exceeds the one supported by Firestore
      if (refresh || !isFirestoreSupportEnabled.value) {
        missing = days
      }
    }

    if (!_shouldSkipApiCallBecauseFirestore()) {
      missing = (refresh) ? days : days.filter(d => {
        return (
          store.days[d] === undefined
          || store.invalidDays[d]
        )
      })
    }
  }

  if (missing.length) {
    logger.debug('missing data for dates: ', missing[0], (missing.length > 1) ? missing[missing.length - 1] : '')
    return fetch(missing[0], missing[missing.length - 1], silent, view, filters, days)
  }

  return Promise.resolve()
}

const fetch = (start, end, silent, view, filters, days = []) => {
  const loadingScreen = useLoadingScreen()
  const { store: coreStore, apiCall } = useCore()

  if (!silent) loadingScreen.show()

  const params = filters
  params.start_date = start
  params.end_date = end
  params.include_unconfirmed = true
  params.version = 3
  params.resources_per_page = coreStore.state.business.resourcesCount || 100

  const url = (view === VIEW_MONTH)
    ? ENDPOINTS.BUSINESS_CALENDAR_MONTHLY
    : ENDPOINTS.BUSINESS_CALENDAR

  return apiCall({
    url,
    urlParams: {
      businessId: coreStore.state.business.business.id,
    },
    params,
    paramsSerializer: params => qs.stringify(params, { indices: false }),
  })
    .then(response => {
      if (view === VIEW_MONTH) {
        saveMonthData(response.data, store, __proxy)
        if (shouldClearCalendarCache.value) {
          clearAllDays(store)
        }
      } else {
        mergeCalendarData(response.data, store, __proxy)
        if (shouldClearCalendarCache.value) {
          clearMonth(store)
          clearDays(store, days) // clear days outside requested range
        }
        _clearInvalidDates(response.data.start_date, response.data.end_date)

        if (!isFirestoreSupportEnabled.value) {
          _calculateOutdatedCalendar(response.data)
        }
      }
    })
    .then(() => {
      loadingScreen.hide()
      _updateVersion()
      return true
    })
}

const fetchDay = (date) => {
  const loadingScreen = useLoadingScreen()
  const { store: coreStore, apiCall } = useCore()

  loadingScreen.show()

  const params = {
    start_date: date,
    end_date: date,
    include_unconfirmed: true,
    version: 3,
  }

  return apiCall({
    url: ENDPOINTS.BUSINESS_CALENDAR,
    urlParams: {
      businessId: coreStore.state.business.business.id,
    },
    params,
    paramsSerializer: params => qs.stringify(params, { indices: false }),
  })
    .then(({ data }) => {
      const _eventProxy = eventProxy(__proxy)

      return {
        ...data,
        bookings: map(pipe(checkAndNormalize, _eventProxy), data.bookings),
      }
    })
    .finally(() => {
      loadingScreen.hide()
      _updateVersion()
    })
}

const clear = (date) => {
  logger.debug('clear', date)
  if (date) {
    const d = ((moment.isMoment(date)) ? date : moment(date)).format(DATE_FORMAT)
    if (store.days[d] !== undefined) {
      store.days[d] = null
      delete store.days[d]
      delete store.invalidDays[d]
    }
  } else {
    store.days = null
    store.days = {}
    store.invalidDays = {}
    checkChangesTimestamp.value = null
  }
}

const getTimeServer = () => {
  const { apiCall } = useCore()

  return apiCall({
    url: ENDPOINTS.SERVER_TIME,
  }).then(({ data }) => {
    return data
  })
}

const getNextStaffer = (date) => {
  const { store: coreStore, apiCall } = useCore()

  return apiCall({
    url: ENDPOINTS.NEXT_STAFFER,
    urlParams: {
      businessId: coreStore.state.business.business.id,
    },
    params: {
      for_date: ((moment.isMoment(date)) ? date : moment(date)).format(DATE_FORMAT),
    },
  }).then(({ data }) => {
    return data
  })
}

const detectChanges = () => {
  // shortcut: firestore is ready and working - skip detect changes
  if (_shouldSkipApiCallBecauseFirestore()) {
    return Promise.resolve()
  }

  const { store: coreStore, apiCall } = useCore()

  // shortcut: calendar `fetch` was not called yet...
  if (!version.value) {
    return Promise.reject(
      new Error('calendar `fetch` was not called yet - detectChanges')
    )
  }

  // shortcut: no business
  if (!coreStore.state.business.business) {
    return Promise.reject(
      new Error('no business - detectChanges')
    )
  }

  const { appIsVisible } = useAppVisibility()
  if (!appIsVisible.value) {
    logger.debug('detect changes paused cause application is not focused')

    latestCalendarUpdateTimestamp.value = null
    observedCalendarIsOutdated.value = false
    return Promise.reject(
      new Error('application is not focus')
    )
  }

  // last `check` or last `fetch`
  const date = checkChangesTimestamp.value || moment(coreStore.state.app.currentTime).utc().format('X')
  logger.debug('checking changes since:', moment.unix(date).utc().format('YYYY-MM-DD HH:mm:ss'))

  return apiCall({
    url: ENDPOINTS.BUSINESS_CALENDAR_CHANGE_DATES,
    urlParams: {
      businessId: coreStore.state.business.business.id,
      timestamp: date, // unix timestamp
    },
  }).then(({ data }) => {
    _checkIfCalendarDataIsOutdatedForObservedDays(data.changed_dates, checkChangesTimestamp.value, observedCalendarDaysRange)
    // `data.business_local_time` is UTC datetime string
    checkChangesTimestamp.value = data.timestamp

    setInvalidDates(data.changed_dates)

    coreStore.commit('app/setCurrentTime', data.business_local_time)
  })
}

const setInvalidDates = (dates) => {
  // shortcut
  if (!dates.length) return

  logger.debug('setting invalid dates', dates)

  dates.forEach(d => {
    const _d = (moment.isMoment(d) ? d : moment(d)).format(DATE_FORMAT)
    if (store.days[_d]) {
      store.invalidDays[_d] = true
    }
  })
}

const setInvalidDatesWithoutCurrent = (date) => {
  const days = [date]
  const diff = difference(Object.keys(store.days), days)
  setInvalidDates(diff)
}

const _clearInvalidDates = (startDate, endDate) => {
  const from = moment.isMoment(startDate)
    ? startDate
    : moment(startDate)

  const till = moment.isMoment(endDate)
    ? endDate
    : moment(endDate)

  const count = Math.max(1, till.diff(from, 'days'))

  const dates = range(0, count).map(i => from.clone().add(i, 'day').format(DATE_FORMAT))

  logger.debug('clearing invalid dates', dates)

  dates.forEach(d => {
    delete store.invalidDays[d]
  })
}

const _setDefault = () => {
  Object.assign(store, clone(DEFAULT_STORE))
  version.value = null

  observedCalendarIsOutdated.value = false
  latestCalendarUpdateTimestamp.value = null
}

const _updateVersion = () => {
  version.value = Date.now()
}

let calendarService = null

export function createCalendarService () {
  logger.trace('created!')

  shouldClearCalendarCache.value = getFlag(CALENDAR_CLEAR_CACHE_FLAG, false)
  _setDefault()

  calendarService = {
    observedDays,
    currentView,
    store,
    version,
    isFirestoreSupportEnabled,
    select,
    fetch,
    fetchDay,
    clear,
    getTimeServer,
    getNextStaffer,
    detectChanges,
    setInvalidDates,
    setInvalidDatesWithoutCurrent,
    _updateVersion,
  }

  return calendarService
}

export function useCalendarService () {
  return calendarService
}

export function destroyCalendarService () {
  calendarService = null
}
